import { type RefObject, useLayoutEffect } from 'react'

type UseInitialScrollToBottom = (params: {
  scrollRef: RefObject<HTMLElement | null>
  /** Whether data is loaded and the scroll container has content laid out. */
  ready: boolean
  /** Whether the scroll has already been done — used as the gate. */
  done: boolean
  /** Called once the scroll has settled (or the safety cap fires). */
  onDone: () => void
}) => void

/**
 * On the first render where `ready` becomes true and `done` is still false:
 *  - synchronously snaps `scrollRef` to the bottom (so the first painted frame
 *    is already at the bottom — no visible "snap" later)
 *  - then keeps re-pinning each frame for up to ~4s to absorb async growth
 *    (e.g. message decryption that fills text over many frames)
 *  - calls `onDone` when scroll height has been stable for 500ms after a
 *    minimum 1s pinning window, or hard-caps after 4s
 *  - exits early (still calls onDone, to avoid re-pinning on the next render)
 *    if the user scrolls UP during the window
 */
export const useInitialScrollToBottom: UseInitialScrollToBottom = ({
  scrollRef,
  ready,
  done,
  onDone,
}) => {
  useLayoutEffect(() => {
    if (done || !ready) {
      return
    }
    const el = scrollRef.current
    if (el) {
      el.scrollTop = el.scrollHeight
    }

    const MIN_FRAMES = 60 // ~1s minimum runtime
    const STABLE_FRAMES = 30 // ~500ms of zero growth before exit
    const MAX_FRAMES = 240 // ~4s hard cap
    let stableFrames = 0
    let framesElapsed = 0
    let lastHeight = -1
    let lastWrittenScrollTop: number | null = null
    let rafId: number | null = null

    const finish = () => {
      rafId = null
      onDone()
    }

    const tick = () => {
      const current = scrollRef.current
      if (!current) {
        finish()
        return
      }
      if (lastWrittenScrollTop !== null && current.scrollTop < lastWrittenScrollTop - 5) {
        finish()
        return
      }
      current.scrollTop = current.scrollHeight
      lastWrittenScrollTop = current.scrollTop
      if (current.scrollHeight === lastHeight) {
        stableFrames++
      } else {
        stableFrames = 0
        lastHeight = current.scrollHeight
      }
      framesElapsed++
      const stable = framesElapsed >= MIN_FRAMES && stableFrames >= STABLE_FRAMES
      if (stable || framesElapsed >= MAX_FRAMES) {
        finish()
        return
      }
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [scrollRef, ready, done, onDone])
}
